import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import AvailabilityGrid from "@/components/AvailabilityGrid";
import { Trophy, User, Baby, CalendarDays, MessageSquare, CheckCircle2, Lock } from "lucide-react";
import CourtLines from "@/components/CourtLines";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyParent = { nome: "", cognome: "", email: "", telefono: "" };
const emptyChild = {
  nome: "", cognome: "", data_nascita: "", codice_fiscale: "",
  indirizzo: "", citta: "", cap: "", provincia: "",
};

export default function RegistrationPage() {
  const [parent, setParent] = useState(emptyParent);
  const [child, setChild] = useState(emptyChild);
  const [availability, setAvailability] = useState({});
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const setP = (k, v) => setParent((s) => ({ ...s, [k]: v }));
  const setC = (k, v) => setChild((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const required = [
      [parent.nome, "Nome genitore"],
      [parent.cognome, "Cognome genitore"],
      [parent.email, "Email genitore"],
      [parent.telefono, "Telefono genitore"],
      [child.nome, "Nome figlio/a"],
      [child.cognome, "Cognome figlio/a"],
      [child.data_nascita, "Data di nascita"],
      [child.codice_fiscale, "Codice fiscale"],
      [child.indirizzo, "Indirizzo"],
      [child.citta, "Città"],
      [child.cap, "CAP"],
      [child.provincia, "Provincia"],
    ];
    for (const [v, label] of required) {
      if (!v || String(v).trim() === "") {
        toast.error(`Campo obbligatorio: ${label}`);
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(parent.email)) {
      toast.error("Email non valida");
      return false;
    }
    if (Object.keys(availability).length === 0) {
      toast.error("Indica almeno una disponibilità nella griglia");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/registrations`, {
        genitore: parent,
        figlio: child,
        disponibilita: availability,
        note,
      });
      setSuccess(res.data);
      toast.success("Iscrizione inviata! Ti contatteremo a breve.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Errore durante l'invio";
      if (status === 409) {
        toast.error(typeof msg === "string" ? msg : "Bambino già iscritto", { duration: 8000 });
      } else {
        toast.error(typeof msg === "string" ? msg : "Errore durante l'invio");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen hero-gradient">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="border-emerald-200 card-warm">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Iscrizione ricevuta!</h2>
              <p className="text-slate-600 mb-1">
                Grazie <strong>{success.genitore.nome}</strong>, l&apos;iscrizione di{" "}
                <strong>{success.figlio.nome} {success.figlio.cognome}</strong> è stata registrata.
              </p>
              <p className="text-xs font-mono text-slate-400 mt-4">
                Codice: {success.id.slice(0, 8).toUpperCase()}
              </p>
              <Button
                className="mt-6 bg-[#15803D] hover:bg-[#166534]"
                onClick={() => {
                  setSuccess(null);
                  setParent(emptyParent);
                  setChild(emptyChild);
                  setAvailability({});
                  setNote("");
                }}
                data-testid="new-registration-btn"
              >
                Nuova iscrizione
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md ring-1 ring-slate-200 overflow-hidden">
              <img src="/logo-ctm.png" alt="Circolo Tennis Montecchio" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <div className="font-bold text-slate-900 leading-tight">Circolo Tennis Montecchio</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Iscrizioni 2026/2027</div>
            </div>
          </div>
          <Link to="/admin" data-testid="admin-link">
            <Button variant="outline" size="sm" className="gap-1.5">
              <Lock className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Login</span>
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#15803D] via-[#166534] to-[#14532D] text-white p-6 md:p-10 mb-8">
          <CourtLines className="absolute inset-0 w-full h-full opacity-25 pointer-events-none" />
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FBBF24]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#C85A32]/15 blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest bg-white/15 backdrop-blur px-3 py-1.5 rounded-full mb-4 border border-white/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FBBF24] animate-pulse" />
              Stagione 2026/2027 · Iscrizioni Aperte
            </div>
            <h1 className="display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-3">
              Iscrizione al Corso di Tennis
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed">
              Compila i dati, indica le disponibilità settimanali con i colori e invia.
              Nessuna registrazione necessaria — bastano pochi minuti.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              <span className="bg-white/15 backdrop-blur rounded-full px-3 py-1.5 border border-white/20">Lun–Ven</span>
              <span className="bg-white/15 backdrop-blur rounded-full px-3 py-1.5 border border-white/20">14:30 – 19:30</span>
              <span className="bg-white/15 backdrop-blur rounded-full px-3 py-1.5 border border-white/20">Slot 30 min</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Parent */}
          <Card className="card-warm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#FDF6F0] flex items-center justify-center">
                  <User className="w-4 h-4 text-[#C85A32]" />
                </div>
                <CardTitle className="text-xl">1. Dati del genitore</CardTitle>
              </div>
              <CardDescription>Chi si occuperà della comunicazione</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome" htmlFor="p-nome">
                <Input id="p-nome" data-testid="parent-nome" value={parent.nome} onChange={(e) => setP("nome", e.target.value)} />
              </Field>
              <Field label="Cognome" htmlFor="p-cognome">
                <Input id="p-cognome" data-testid="parent-cognome" value={parent.cognome} onChange={(e) => setP("cognome", e.target.value)} />
              </Field>
              <Field label="Email" htmlFor="p-email">
                <Input id="p-email" type="email" data-testid="parent-email" value={parent.email} onChange={(e) => setP("email", e.target.value)} />
              </Field>
              <Field label="Telefono" htmlFor="p-tel">
                <Input id="p-tel" type="tel" data-testid="parent-telefono" value={parent.telefono} onChange={(e) => setP("telefono", e.target.value)} />
              </Field>
            </CardContent>
          </Card>

          {/* Child */}
          <Card className="card-warm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#FDF6F0] flex items-center justify-center">
                  <Baby className="w-4 h-4 text-[#C85A32]" />
                </div>
                <CardTitle className="text-xl">2. Dati del figlio/a</CardTitle>
              </div>
              <CardDescription>Chi frequenterà il corso</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome" htmlFor="c-nome">
                <Input id="c-nome" data-testid="child-nome" value={child.nome} onChange={(e) => setC("nome", e.target.value)} />
              </Field>
              <Field label="Cognome" htmlFor="c-cognome">
                <Input id="c-cognome" data-testid="child-cognome" value={child.cognome} onChange={(e) => setC("cognome", e.target.value)} />
              </Field>
              <Field label="Data di nascita" htmlFor="c-dn">
                <Input id="c-dn" type="date" data-testid="child-data-nascita" value={child.data_nascita} onChange={(e) => setC("data_nascita", e.target.value)} />
              </Field>
              <Field label="Codice Fiscale" htmlFor="c-cf">
                <Input id="c-cf" data-testid="child-codice-fiscale" value={child.codice_fiscale} onChange={(e) => setC("codice_fiscale", e.target.value.toUpperCase())} maxLength={16} />
              </Field>
              <Separator className="md:col-span-2 my-1" />
              <div className="md:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  Residenza del figlio/a
                </p>
              </div>
              <Field label="Indirizzo" htmlFor="c-ind" className="md:col-span-2">
                <Input id="c-ind" data-testid="child-indirizzo" value={child.indirizzo} onChange={(e) => setC("indirizzo", e.target.value)} placeholder="Via / Piazza e numero civico" />
              </Field>
              <Field label="Città" htmlFor="c-city">
                <Input id="c-city" data-testid="child-citta" value={child.citta} onChange={(e) => setC("citta", e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="CAP" htmlFor="c-cap">
                  <Input id="c-cap" data-testid="child-cap" value={child.cap} onChange={(e) => setC("cap", e.target.value)} maxLength={5} />
                </Field>
                <Field label="Provincia" htmlFor="c-prov">
                  <Input id="c-prov" data-testid="child-provincia" value={child.provincia} onChange={(e) => setC("provincia", e.target.value.toUpperCase())} maxLength={2} placeholder="RM" />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card className="card-warm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#FDF6F0] flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 text-[#C85A32]" />
                </div>
                <CardTitle className="text-xl">3. Disponibilità settimanale</CardTitle>
              </div>
              <CardDescription>
                Colora le fasce di 30 minuti dal lunedì al venerdì (14:30 – 19:30)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AvailabilityGrid value={availability} onChange={setAvailability} />
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="card-warm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#FDF6F0] flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-[#C85A32]" />
                </div>
                <CardTitle className="text-xl">4. Note (facoltativo)</CardTitle>
              </div>
              <CardDescription>Esigenze particolari, richieste, allergie, livello di gioco</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="note-textarea"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Scrivi qui eventuali note per la segreteria..."
                rows={4}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-2">
            <p className="text-xs text-slate-500 flex-1">
              Verrai contattato via email o telefono per confermare orario e gruppo del corso.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-registration"
              size="lg"
              className="bg-[#C85A32] hover:bg-[#a94a28] text-white font-semibold shadow-lg shadow-[#C85A32]/25 rounded-full px-8"
            >
              {submitting ? "Invio in corso..." : "Conferma ed invia iscrizione"}
            </Button>
          </div>
        </form>
      </main>

      <footer className="max-w-4xl mx-auto px-4 py-8 text-center text-xs text-slate-400">
        © 2026 Circolo Tennis Montecchio — Modulo iscrizioni
      </footer>
    </div>
  );
}

function Field({ label, htmlFor, className = "", children }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
